import React, { useEffect, useCallback } from "react";
import { Photo } from "../types";
import "../styles/ImageViewer.css";
import ImageLoader from "./ImageLoader";
import { Language, imageViewerTranslations } from "../translations";

interface ImageViewerProps {
  photos: Photo[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
  showDownloadButton?: boolean;
  onDownloadPhoto?: (url: string, filename: string) => void;
  language?: Language;
}

const ImageViewer: React.FC<ImageViewerProps> = ({
  photos,
  currentIndex,
  onClose,
  onNavigate,
  showDownloadButton = false,
  onDownloadPhoto,
  language = "en",
}) => {
  const translations = imageViewerTranslations[language];
  // const [loading, setLoading] = useState(true);

  // Get current photo
  const currentPhoto = photos[currentIndex];
  const totalPhotos = photos.length;

  // Navigation handlers
  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      onNavigate(currentIndex - 1);
    } else {
      // Wrap around to the last photo
      onNavigate(photos.length - 1);
    }
  }, [currentIndex, photos.length, onNavigate]);

  const goToNext = useCallback(() => {
    if (currentIndex < photos.length - 1) {
      onNavigate(currentIndex + 1);
    } else {
      // Wrap around to the first photo
      onNavigate(0);
    }
  }, [currentIndex, photos.length, onNavigate]);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowLeft":
          goToPrevious();
          break;
        case "ArrowRight":
          goToNext();
          break;
        case "Escape":
          onClose();
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [goToPrevious, goToNext, onClose]);

  // Extract the file name from the URL
  const getImageName = (url: string) => {
    const pathParts = url.split("/");
    return pathParts[pathParts.length - 1];
  };

  return (
    <div className="image-viewer-overlay" onClick={onClose}>
      <div
        className="image-viewer-content"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="close-button" onClick={onClose}>
          ×
        </button>

        <div className="image-navigation" onClick={onClose}>
          <button
            className="nav-button prev"
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
          >
            ‹
          </button>

          <div className="image-container" onClick={onClose}>
            {/* {loading && <div className="loading-spinner">Loading...</div>} */}
            {currentPhoto.url.toLowerCase().endsWith(".mp4") ||
            currentPhoto.url.toLowerCase().endsWith(".mov") ? (
              <video
                id="video1"
                style={{
                  width: "100%",
                  maxHeight: "100vh",
                  paddingBottom: 100,
                }}
                controls={true}
              >
                <source src={currentPhoto.url} type="video/mp4" />
                Your browser does not support HTML5 video.
              </video>
            ) : (
              <ImageLoader
                src={currentPhoto.url}
                thumbnailSrc={currentPhoto.thumbnailUrl}
                alt={`Photo ${currentPhoto.id}`}
                // onLoad={() => setLoading(false)}
                // className={loading ? 'loading' : 'loaded'}
                onClick={(e) => e.stopPropagation()}
                language={language}
              />
            )}
          </div>

          <button
            className="nav-button next"
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
          >
            ›
          </button>
        </div>

        <div className="image-info">
          <div className="image-name">{getImageName(currentPhoto.url)}</div>
          <div className="image-counter">
            {currentIndex + 1} / {totalPhotos}
          </div>
          <div className="image-time">
            {new Date(currentPhoto.takenAt).toLocaleString(undefined, {
              timeZone: "Europe/Warsaw",
            })}
          </div>
          {showDownloadButton && onDownloadPhoto && (
            <button
              className="download-button-detail"
              onClick={() =>
                onDownloadPhoto(
                  currentPhoto.url,
                  getImageName(currentPhoto.url),
                )
              }
            >
              {translations.downloadButtonText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageViewer;
